<template>
  <div class="po-confirm">
    <!-- 顶部查询与操作栏（对标 waitChk.vue 与 stk_check.vue） -->
    <header class="query-header" :aria-label="v_title">
      <button type="button" class="back-button" aria-label="返回功能首页" :disabled="busy" @click="onClickLeft">
        <van-icon name="arrow-left" size="20" aria-hidden="true" />
      </button>
      <form class="search-form" role="search" @submit.prevent="onSearch">
        <div class="search-input">
          <van-icon name="search" size="14" aria-hidden="true" />
          <input
            v-model="v_search"
            :disabled="busy"
            type="search"
            enterkeyhint="search"
            aria-label="搜索采购订单、供应商或送货单号"
            placeholder="采购订单 / 供应商 / 送货单号"
            @keydown.enter.prevent="onSearch"
          />
          <button v-if="v_search" type="button" class="clear-button" aria-label="清空搜索" :disabled="busy || listLoading" @click="clearSearch">
            <van-icon name="cross" size="16" />
          </button>
        </div>
        <button type="submit" class="header-action-button" aria-label="搜索" :disabled="busy || listLoading">
          <van-icon name="search" size="16" aria-hidden="true" />
        </button>
        <button type="button" class="header-action-button" aria-label="刷新" :disabled="listLoading || busy" @click="onSearch">
          <van-icon name="replay" size="16" :class="{ 'is-spinning': listLoading }" aria-hidden="true" />
        </button>
      </form>
    </header>

    <!-- 主列表区域 -->
    <main ref="listContent" class="list-content" :aria-label="v_title" :aria-busy="listLoading" @scroll.passive="onListScroll">
      <div v-if="listError" class="state-panel" role="alert">
        <p>{{ listError }}</p>
        <van-button class="primary-button" :disabled="busy || listLoading" @click="onSearch">重新查询</van-button>
      </div>
      <div v-else-if="listLoading && !tabData.length" class="state-panel" role="status">
        <van-loading size="24px" />
        <span>正在查询送货单…</span>
      </div>
      <div v-else-if="!tabData.length" class="state-panel" role="status">
        <van-icon name="description" size="40" color="#999999" />
        <p>暂无送货单</p>
      </div>
      <template v-else>
        <article
          v-for="(item, index) in tabData"
          :key="item.IDSEQ"
          class="chk-card"
          :aria-label="'送货单 ' + item.IDNO"
        >
          <!-- 卡片头部：左侧序号、单号与分类，右侧状态标签 -->
          <header class="card-header">
            <div class="card-title-group">
              <span class="card-seq">{{ index + 1 }}</span>
              <span class="card-tag">送货单</span>
              <h2 class="card-idno">{{ item.IDNO || '—' }}</h2>
            </div>
            <span class="status-tag" :class="item.STFG === 'Y' ? 'is-audited' : 'is-pending'">
              {{ item.STFG === 'Y' ? '已审核' : '未审核' }}
            </span>
          </header>

          <!-- 单据核心信息区：自适应网格，字体排版对标 waitChk.vue -->
          <dl class="document-info">
            <div>
              <dt>送货日期</dt>
              <dd>{{ item.SHPDTTM || '—' }}</dd>
            </div>
            <div>
              <dt>采购单号</dt>
              <dd>{{ item.PONO || '—' }}</dd>
            </div>
            <div class="full-row">
              <dt>供应商</dt>
              <dd class="highlight-text">{{ item.SUPNM || '—' }}</dd>
            </div>
            <div class="full-row">
              <dt>订单编码</dt>
              <dd>{{ item.ORDNO || '—' }}</dd>
            </div>
            <div class="full-row count-and-expand-row">
              <div class="count-item-wrap">
                <dt>物料种数</dt>
                <dd class="count-tag-wrap">
                  <span class="count-badge">{{ item.ROWCNT == null ? '0' : item.ROWCNT }}</span>
                </dd>
              </div>
              <!-- “展开”按钮放置在物料种数同行右侧 -->
              <button
                type="button"
                class="toggle-expand-btn"
                :aria-expanded="isExpanded(item)"
                @click="toggleExpand(item)"
              >
                <span>{{ isExpanded(item) ? '收起' : '展开' }}</span>
                <van-icon :name="isExpanded(item) ? 'arrow-up' : 'arrow-down'" />
              </button>
            </div>
          </dl>

          <!-- 就地展开的送货明细清单区域 -->
          <section v-if="isExpanded(item)" class="expanded-details" :aria-label="'送货单 ' + item.IDNO + ' 送货明细'">
            <div class="details-header">
              <span class="details-title">送货明细清单</span>
              <span class="details-badge">共 {{ detailsFor(item).length }} 项物料</span>
            </div>

            <!-- 明细加载状态 -->
            <div v-if="dtlLoadingMap[item.IDSEQ]" class="state-panel-inline">
              <van-loading size="20px">正在加载送货明细…</van-loading>
            </div>

            <!-- 空明细提示 -->
            <div v-else-if="!detailsFor(item).length" class="empty-dtl-notice">
              <van-icon name="warning-o" color="#999999" size="18" />
              <span>暂无送货明细记录</span>
            </div>

            <!-- 物料子卡片列表 -->
            <div v-else class="dtl-card-list">
              <article
                v-for="(row, rowIdx) in detailsFor(item)"
                :key="row.IDSEQ + '_' + (row.LSTSEQ || rowIdx)"
                class="dtl-item-card"
              >
                <!-- 物料行头部 -->
                <header class="dtl-heading">
                  <span class="dtl-seq-badge">{{ rowIdx + 1 }}</span>
                  <div class="dtl-mat-info">
                    <div class="dtl-mat-title">
                      <span class="dtl-mat-no">{{ row.MATNO || '—' }}</span>
                      <span class="dtl-mat-nm">{{ row.MATNM || '未命名物料' }}</span>
                    </div>
                    <p class="dtl-mat-spec">{{ row.SPECNM || '暂无规格' }}</p>
                  </div>
                </header>

                <!-- 数据网格：送货数量、采购数量、仓库名称、仓位名称 -->
                <div class="dtl-meta-grid">
                  <div class="meta-item send-qty-item">
                    <span class="meta-label">送货数量</span>
                    <span class="meta-value send-val">{{ quantityValue(row.POQTY == null || row.POQTY === '' ? (row.QTY || '0') : row.POQTY) }} {{ row.UNITNM || '' }}</span>
                  </div>
                  <div class="meta-item">
                    <span class="meta-label">采购数量</span>
                    <span class="meta-value">{{ row.QTY == null || row.QTY === '' ? '—' : row.QTY }} {{ row.UNITNM || '' }}</span>
                  </div>
                  <div class="meta-item">
                    <span class="meta-label">仓库名称</span>
                    <span class="meta-value">{{ row.STKNM || '—' }}</span>
                  </div>
                  <div class="meta-item">
                    <span class="meta-label">仓位名称</span>
                    <span class="meta-value">{{ row.PLCNM || '—' }}</span>
                  </div>
                </div>

                <div v-if="row.RMK" class="dtl-remark-row">
                  <span class="meta-label">备注：</span>
                  <span class="remark-text">{{ row.RMK }}</span>
                </div>

                <!-- 物料图片区域（对标 stk_check.vue） -->
                <p v-if="materialImageLoading[materialImageKey(row)]" class="material-image-status" role="status">正在读取物料图片…</p>
                <div v-else-if="materialImageErrors[materialImageKey(row)]" class="material-image-status" role="alert">
                  <span>物料图片读取失败</span><button type="button" @click="loadMaterialImages([row])">重新读取</button>
                </div>
                <div v-if="materialImages(row).length" class="material-images" role="group" :aria-label="(row.MATNO || '物料') + '图片'">
                  <button
                    v-for="(src, imageIndex) in materialImages(row)"
                    :key="src"
                    type="button"
                    class="material-thumbnail"
                    :aria-label="'查看' + (row.MATNO || '物料') + '第' + (imageIndex + 1) + '张图片'"
                    @click="previewMaterialImages(row, imageIndex, $event)"
                  >
                    <van-image :src="src" fit="contain" :alt="(row.MATNM || row.MATNO || '物料') + '图片 ' + (imageIndex + 1)">
                      <template slot="error"><span class="material-image-error">加载失败</span></template>
                    </van-image>
                  </button>
                </div>
              </article>
            </div>
          </section>

          <!-- 卡片底部操作按钮：左边【编辑】、中间【打印】、右边【审核】（实色紧凑胶囊） -->
          <footer class="card-footer-actions">
            <van-button
              type="info"
              size="small"
              class="action-btn edit-btn"
              :disabled="editBool || busy || listLoading || isPreparingPrint || isPrinting"
              @click.stop="openEditDoc(item)"
            >
              编辑
            </van-button>
            <van-button
              type="primary"
              size="small"
              class="action-btn print-btn"
              :disabled="isPreparingPrint || isPrinting || busy"
              @click.stop="prtRec(item)"
            >
              打印
            </van-button>
            <van-button
              v-if="item.STFG === 'N'"
              type="primary"
              size="small"
              class="action-btn audit-btn"
              :disabled="cfmBool || busy || listLoading"
              :loading="auditingId === String(item.IDSEQ)"
              @click.stop="stfgRec(item, 'Y')"
            >
              审核
            </van-button>
          </footer>
        </article>
      </template>
    </main>

    <!-- 返回顶部按钮 -->
    <div v-show="topShow" class="go_top_btn" @click="goTop">
      <van-icon name="arrow-up" size="24px" color="#235EFF" />
    </div>

    <!-- 送货单编辑弹窗（处理分批送货、数量修改） -->
    <van-popup
      v-if="editPopupVisible"
      v-model="editPopupVisible"
      position="bottom"
      round
      :overlay="true"
      :close-on-click-overlay="true"
      class="edit-popup"
      :style="{ maxHeight: '88vh', display: 'flex', flexDirection: 'column' }"
    >
      <div class="edit-popup-header">
        <span class="edit-popup-title">编辑送货明细（{{ editDoc ? editDoc.IDNO : '' }}）</span>
        <button type="button" class="close-icon-btn" aria-label="关闭" @click.stop="closeEditPopup">
          <van-icon name="cross" size="20" />
        </button>
      </div>

      <div class="edit-popup-content">
        <div v-if="editLoading" class="state-panel-inline">
          <van-loading size="20px">加载中…</van-loading>
        </div>
        <div v-else-if="!editList.length" class="empty-dtl-notice">
          <span>暂无明细数据</span>
        </div>
        <div v-else class="edit-item-list">
          <div
            v-for="(row, idx) in editList"
            :key="row.IDSEQ + '_' + (row.LSTSEQ || idx)"
            class="edit-item-card"
          >
            <div class="edit-item-heading">
              <span class="dtl-seq-badge">{{ idx + 1 }}</span>
              <div class="edit-mat-title">
                <span class="edit-mat-no">{{ row.MATNO }}</span>
                <span class="edit-mat-nm">{{ row.MATNM }}</span>
              </div>
            </div>
            <p v-if="row.SPECNM" class="edit-mat-spec">{{ row.SPECNM }}</p>

            <div class="edit-fields-grid">
              <div class="edit-field-item">
                <span class="field-label">采购数量:</span>
                <span class="field-val">{{ row.QTY }} {{ row.UNITNM || '' }}</span>
              </div>
              <div class="edit-field-item">
                <span class="field-label">仓库:</span>
                <span class="field-val">{{ row.STKNM || '—' }}</span>
              </div>
              <div class="edit-field-item edit-field-input">
                <label :for="'send-qty-' + idx" class="field-label">送货数量:</label>
                <div class="input-unit-wrap">
                  <input
                    :id="'send-qty-' + idx"
                    v-model="row.POQTY"
                    type="number"
                    min="0"
                    step="any"
                    placeholder="输入送货数"
                    class="edit-input"
                  />
                  <span v-if="row.UNITNM" class="input-unit">{{ row.UNITNM }}</span>
                </div>
              </div>
              <div class="edit-field-item edit-field-input">
                <label :for="'plc-nm-' + idx" class="field-label">仓位名称:</label>
                <input
                  :id="'plc-nm-' + idx"
                  v-model="row.PLCNM"
                  type="text"
                  placeholder="输入仓位"
                  class="edit-input"
                />
              </div>
            </div>

            <!-- 编辑弹窗内的物料图片区域 -->
            <p v-if="materialImageLoading[materialImageKey(row)]" class="material-image-status" role="status">正在读取物料图片…</p>
            <div v-else-if="materialImageErrors[materialImageKey(row)]" class="material-image-status" role="alert">
              <span>物料图片读取失败</span><button type="button" @click="loadMaterialImages([row])">重新读取</button>
            </div>
            <div v-if="materialImages(row).length" class="material-images" role="group" :aria-label="(row.MATNO || '物料') + '图片'">
              <button
                v-for="(src, imageIndex) in materialImages(row)"
                :key="src"
                type="button"
                class="material-thumbnail"
                :aria-label="'查看' + (row.MATNO || '物料') + '第' + (imageIndex + 1) + '张图片'"
                @click="previewMaterialImages(row, imageIndex, $event)"
              >
                <van-image :src="src" fit="contain" :alt="(row.MATNM || row.MATNO || '物料') + '图片 ' + (imageIndex + 1)">
                  <template slot="error"><span class="material-image-error">加载失败</span></template>
                </van-image>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="edit-popup-footer">
        <van-button
          plain
          size="small"
          class="popup-cancel-btn"
          @click.stop="closeEditPopup"
        >
          取消
        </van-button>
        <van-button
          type="primary"
          size="small"
          class="popup-save-btn"
          :loading="savingEdit"
          @click="saveEditDoc"
        >
          保存
        </van-button>
      </div>
    </van-popup>

    <!-- 条码打印预览弹窗 -->
    <van-popup
      v-model="showPreview"
      position="bottom"
      :close-on-click-overlay="!isPrinting"
      get-container="body"
      class="po-print-popup"
      :style="{ height: '100%', width: '100%', background: '#f5f5f5' }"
    >
      <div class="po-print-preview">
        <van-nav-bar title="打印预览">
          <template #left>
            <button type="button" class="po-print-action" :disabled="isPrinting" @click="showPreview = false">取消</button>
          </template>
          <template #right>
            <button type="button" class="po-print-action" :disabled="isPrinting" @click="confirmPrint">
              {{ isPrinting ? '打印中…' : '确认打印' }}
            </button>
          </template>
        </van-nav-bar>
        <div class="po-print-content">
          <section class="po-print-counts" aria-label="各物料打印份数">
            <div class="po-print-counts-heading">
              <span>各物料打印份数</span>
              <span class="po-print-total" aria-live="polite">
                {{ printItems.length }} 种物料 · 合计 {{ totalPrintCopies === null ? '—' : totalPrintCopies }} 张
              </span>
            </div>
            <label v-for="(item, index) in printItems" :key="item.key" class="po-print-count-row">
              <span class="po-print-material">
                <span class="po-print-material-code">{{ index + 1 }}. {{ item.label.matno }}</span>
                <span class="po-print-material-name">{{ item.label.matnm }}</span>
              </span>
              <input
                class="po-print-location"
                :value="item.label.plcnm"
                placeholder="仓位"
                :aria-label="item.label.matno + ' 仓位'"
                :disabled="isPrinting"
                @input="updatePrintLocation(item, $event.target.value)"
              />
              <span class="po-print-count-input">
                <input
                  v-model="item.copies"
                  type="number"
                  inputmode="numeric"
                  min="1"
                  max="100"
                  step="1"
                  :disabled="isPrinting"
                  :aria-label="item.label.matno + ' ' + item.label.matnm + ' 打印份数'"
                />
                <span>份</span>
              </span>
            </label>
          </section>
          <div v-for="(item, index) in printItems" :key="item.key" class="po-print-label">
            <p>{{ index + 1 }}. {{ item.label.matno }} · {{ item.copies }} 份</p>
            <img :src="item.previewUrl" :alt="item.label.matno + ' ' + item.label.matnm + ' 标签预览'" />
          </div>
        </div>
      </div>
    </van-popup>

    <!-- 物料图片大图预览（对标 stk_check.vue） -->
    <van-image-preview
      v-model="imagePreviewOpen"
      :images="previewImages"
      :start-position="previewImageIndex"
      :close-on-popstate="false"
      get-container="body"
      class-name="qa-material-preview"
      role="dialog"
      aria-label="物料图片预览"
      aria-modal="true"
      @close="closeMaterialPreview"
      @keydown.esc.native.stop.prevent="closeMaterialPreview"
    >
      <template slot="cover">
        <button
          ref="imagePreviewClose"
          type="button"
          class="material-preview-close"
          aria-label="关闭图片预览"
          @click.stop="closeMaterialPreview"
        >
          关闭
        </button>
      </template>
    </van-image-preview>
  </div>
</template>

<script>
import QRCode from 'qrcode'
import { getDate } from '@/api/tools'
import { getMaterialImageUrls, materialImagePath } from '@/api/materialImages'
import {
  isSupported as btSupported,
  getUnsupportedReason,
  printFabBarcode,
  renderPreview as renderFabPreview,
} from '@/api/fabBarcodePrint'

function parsePrintCopies(value) {
  const text = String(value).trim()
  const copies = Number(text)
  return /^\d+$/.test(text) && Number.isInteger(copies) && copies >= 1 && copies <= 100 ? copies : null
}

export default {
  name: 'PoConfirm',
  data() {
    return {
      v_title: '采购送货审核',
      v_search: '',
      listLoading: false,
      listError: '',
      auditingId: '',

      v_usernm: this.$cookies.get('v_usernm'),
      v_username: this.$cookies.get('v_telno'),
      v_corpid: this.$cookies.get('v_corpid'),

      tabData: [],
      cfmBool: true,
      editBool: false,

      // 展开与明细状态
      expandedIds: [],
      detailsMap: {},
      dtlLoadingMap: {},

      // 编辑弹窗相关
      editPopupVisible: false,
      editDoc: null,
      editList: [],
      editLoading: false,
      savingEdit: false,

      // 打印预览与蓝牙相关
      showPreview: false,
      printItems: [],
      isPreparingPrint: false,
      isPrinting: false,

      // 物料图片相关（对标 stk_check.vue）
      materialImageSets: {},
      materialImageLoading: {},
      materialImageErrors: {},
      imagePreviewOpen: false,
      previewImages: [],
      previewImageIndex: 0,

      // 滚动与返回顶部
      topShow: false,
    }
  },
  mounted() {
    this.initData()
    this.onSearch()
  },
  computed: {
    busy() {
      return !!this.auditingId || this.isPreparingPrint || this.isPrinting || this.savingEdit
    },
    totalPrintCopies() {
      const counts = this.printItems.map(item => parsePrintCopies(item.copies))
      return counts.includes(null) ? null : counts.reduce((total, count) => total + count, 0)
    },
  },
  methods: {
    quantityValue(value) {
      const text = String(value == null ? '' : value).trim()
      const match = text.match(/^-?\d+(?:\.\d+)?/)
      return match ? match[0] : text
    },

    isExpanded(item) {
      return this.expandedIds.includes(String(item.IDSEQ))
    },

    toggleExpand(item) {
      const id = String(item.IDSEQ)
      const idx = this.expandedIds.indexOf(id)
      if (idx >= 0) {
        this.expandedIds.splice(idx, 1)
      } else {
        this.expandedIds.push(id)
        if (!this.detailsMap[id] || !this.detailsMap[id].length) {
          this.getDtlList(item.IDSEQ)
        }
      }
    },

    detailsFor(item) {
      return this.detailsMap[String(item.IDSEQ)] || []
    },

    materialImageKey(row) {
      if (row.MATNO) return 'mat:' + row.MATNO
      const id = row.MATIDSEQ || row.MAT_IDSEQ
      return id ? 'id:' + id : 'line:' + (row.IDSEQ + '_' + row.LSTSEQ)
    },

    materialImages(row) {
      const images = this.materialImageSets[this.materialImageKey(row)]
      if (images) return images
      const mainImage = materialImagePath(row.IMGPATH)
      return mainImage ? [mainImage] : []
    },

    async loadMaterialImages(rows) {
      if (!Array.isArray(rows) || !rows.length) return
      const unique = new Map(rows.filter(r => r.MATNO || r.MATIDSEQ || r.MAT_IDSEQ).map(row => [this.materialImageKey(row), row]))
      await Promise.all(Array.from(unique, async ([key, row]) => {
        if (this.materialImageLoading[key] || this.materialImageSets[key]) return
        this.$set(this.materialImageLoading, key, true)
        this.$set(this.materialImageErrors, key, '')
        try {
          const images = await getMaterialImageUrls(row, this.v_corpid, async (data, url) => {
            const res = await this.$axios({
              method: 'post',
              url: url || 'xuper_auth/getBaseData',
              data,
            })
            return (res && res.result) || []
          })
          this.$set(this.materialImageSets, key, images)
        } catch (error) {
          this.$set(this.materialImageErrors, key, error.message || '读取失败')
        } finally {
          this.$set(this.materialImageLoading, key, false)
        }
      }))
    },

    previewMaterialImages(row, index, event) {
      const images = this.materialImages(row)
      if (!Number.isInteger(index) || index < 0 || index >= images.length) return
      this.previewImages = images
      this.previewImageIndex = index
      this._materialPreviewTrigger = event && event.currentTarget
      this.imagePreviewOpen = true
      this.$nextTick(() => {
        if (this.$refs.imagePreviewClose) this.$refs.imagePreviewClose.focus()
      })
    },

    closeMaterialPreview() {
      this.imagePreviewOpen = false
      const trigger = this._materialPreviewTrigger
      this._materialPreviewTrigger = null
      if (trigger && trigger.isConnected) this.$nextTick(() => trigger.focus())
    },

    async getDtlList(idseq) {
      const id = String(idseq)
      this.$set(this.dtlLoadingMap, id, true)
      try {
        const res = await this.$axios({
          method: 'post',
          url: 'xuper_auth/getBaseData',
          data: {
            p_key: 'idseq',
            p_value: idseq,
            p_table: 'V_PO_SENTDTL_QRY',
            p_cond: ' order by adddttm desc',
          },
        })
        if (String(res.code) === '200' && Array.isArray(res.result)) {
          this.$set(this.detailsMap, id, res.result)
          this.loadMaterialImages(res.result)
        } else {
          this.$toast.fail((res && res.result) || '获取送货明细失败')
        }
      } catch (err) {
        this.$toast.fail('明细加载失败：' + (err.message || '网络异常'))
      } finally {
        this.$set(this.dtlLoadingMap, id, false)
      }
    },

    closeEditPopup() {
      this.editPopupVisible = false
    },

    // 打开送货单编辑弹窗（处理分批送货、数量调整）
    async openEditDoc(item) {
      if (this.busy || this.isPreparingPrint || this.isPrinting) return
      this.showPreview = false
      this.editDoc = item
      this.editPopupVisible = true
      this.editLoading = true
      this.editList = []
      try {
        const res = await this.$axios({
          method: 'post',
          url: 'xuper_auth/getBaseData',
          data: {
            p_key: 'idseq',
            p_value: item.IDSEQ,
            p_table: 'V_PO_SENTDTL_QRY',
            p_cond: ' order by adddttm desc',
          },
        })
        if (String(res.code) === '200' && Array.isArray(res.result)) {
          this.editList = res.result.map(row => {
            const rawVal = row.POQTY == null || row.POQTY === '' ? (row.QTY || '') : row.POQTY
            // 提取纯数字部分，防止后端视图带入单位如 "650 ( 个 )" 导致 <input type="number"> 报错
            const match = String(rawVal).match(/^-?\d+(\.\d+)?/)
            const cleanQty = match ? match[0] : ''
            return {
              ...row,
              POQTY: cleanQty,
            }
          })
          this.loadMaterialImages(this.editList)
        } else {
          this.$toast.fail((res && res.result) || '明细加载失败')
        }
      } catch (err) {
        this.$toast.fail('明细加载失败：' + (err.message || '网络异常'))
      } finally {
        this.editLoading = false
      }
    },

    // 保存编辑修改
    async saveEditDoc() {
      if (this.savingEdit || !this.editDoc) return

      for (let i = 0; i < this.editList.length; i++) {
        const row = this.editList[i]
        if (row.POQTY === null || row.POQTY === undefined || String(row.POQTY).trim() === '') {
          this.$dialog.alert({
            title: '提示',
            message: `第 ${i + 1} 项明细（${row.MATNM || row.MATNO}）送货数量不能为空`,
            theme: 'round-button',
          })
          return
        }
        const qtyNum = Number(row.POQTY)
        if (!Number.isFinite(qtyNum) || qtyNum < 0) {
          this.$dialog.alert({
            title: '提示',
            message: `第 ${i + 1} 项明细送货数量必须为大于或等于零的数字`,
            theme: 'round-button',
          })
          return
        }
      }

      this.savingEdit = true
      const loadingToast = this.$toast.loading({ message: '正在保存...', duration: 0, forbidClick: true })
      try {
        const payload = this.editList.map(row => ({
          ...row,
          SHPQTY: row.POQTY, // 兼容后台字段
        }))

        const res = await this.$axios({
          method: 'post',
          url: 'xuper_auth/batDtlSave2',
          data: {
            p_json: JSON.stringify(payload),
            p_table: 'po_sentDTL',
            p_idseq: this.editDoc.IDSEQ,
            p_action: 'S',
          },
        })
        if (!res || String(res.code) !== '200') {
          throw new Error((res && res.result) || '保存失败')
        }

        this.$toast.success('保存成功')
        this.editPopupVisible = false
        // 刷新当前单据缓存及主列表
        this.$set(this.detailsMap, String(this.editDoc.IDSEQ), payload)
        await this.onSearch()
      } catch (err) {
        this.$dialog.alert({ title: '保存失败', message: err.message || '网络异常，请重试' })
      } finally {
        loadingToast.clear()
        this.savingEdit = false
      }
    },

    async updatePrintLocation(item, value) {
      if (this.isPrinting) return
      this.$set(item.label, 'plcnm', value)
      const revision = (item.previewRevision || 0) + 1
      this.$set(item, 'previewRevision', revision)
      try {
        const url = await renderFabPreview({ ...item.label })
        if (item.previewRevision === revision) item.previewUrl = url
      } catch (error) {
        if (item.previewRevision === revision) this.$toast.fail('仓位预览更新失败，请重新输入')
      }
    },

    // 确认打印（蓝牙）
    async confirmPrint() {
      if (this.isPrinting || this.isPreparingPrint || this.printItems.length === 0) return
      const jobs = []
      for (const item of this.printItems) {
        const copies = parsePrintCopies(item.copies)
        if (copies === null) {
          this.$dialog.alert({
            title: '打印份数',
            message: item.label.matno + ' ' + item.label.matnm + '：请输入 1 到 100 的整数',
          })
          return
        }
        jobs.push({ label: item.label, copies })
      }
      if (!btSupported()) {
        this.$dialog.alert({ title: '不支持蓝牙打印', message: getUnsupportedReason() })
        return
      }
      const total = jobs.reduce((sum, item) => sum + item.copies, 0)
      this.isPrinting = true
      const pToast = this.$toast.loading({ message: '正在打印...', duration: 0, forbidClick: true })
      let sent = 0
      try {
        for (const job of jobs) {
          for (let copy = 0; copy < job.copies; copy++) {
            pToast.message = '正在发送 ' + (sent + 1) + '/' + total + ' 张…'
            await printFabBarcode(job.label)
            sent++
            if (sent < total) {
              pToast.message = '已发送 ' + sent + '/' + total + ' 张，等待打印机定位…'
              await new Promise(resolve => setTimeout(resolve, 2000))
            }
          }
        }
      } catch (err) {
        if (err && err.name === 'NotFoundError') return
        this.$dialog.alert({
          title: '打印失败',
          message: '已发送 ' + sent + '/' + total + ' 张，批量打印已停止。' + ((err && err.message) || '连接打印机失败'),
        })
        return
      } finally {
        pToast.clear()
        this.isPrinting = false
      }
      this.showPreview = false
      this.$toast.success('已发送 ' + sent + ' 张，请确认纸面打印结果')
    },

    // 预览打印
    async prtRec(item) {
      if (this.isPreparingPrint || this.isPrinting) return
      this.editPopupVisible = false
      this.isPreparingPrint = true
      this.showPreview = false
      this.printItems = []
      const toast = this.$toast.loading({ message: '生成预览...', duration: 0, forbidClick: true })
      try {
        const res = await this.$axios({
          method: 'post',
          url: 'xuper_auth/getBaseData',
          data: {
            p_key: 'idseq',
            p_value: item.IDSEQ,
            p_table: 'V_PO_SENTDTL_QRY',
            p_cond: ' order by adddttm desc',
          },
        })
        if (String(res.code) !== '200' || !Array.isArray(res.result)) {
          throw new Error(typeof res.result === 'string' ? res.result : '送货明细加载失败')
        }
        if (res.result.length === 0) throw new Error('该单据没有可打印的物料')
        const items = await Promise.all(
          res.result.map(async (row, index) => {
            const v_code = String(row.MATNO || '').trim()
            if (!v_code) throw new Error('第 ' + (index + 1) + ' 条明细缺少物料编码，无法打印')
            const v_label = {
              supnm: row.SUPNM || '',
              refpono: row.PONO || '',
              matnm: row.MATNM || '',
              specnm: row.SPECNM || '',
              matno: row.MATNO || '',
              idno: row.IDNO || '',
              adddttm: row.ADDDTTM || '',
              adduser: row.ADDUSER || '',
              punit: row.UNITNM || '',
              poqty: row.QTY || '',
              sendqty: row.POQTY || '',
              stknm: row.STKNM || '',
              plcnm: row.PLCNM || '',
              rmk: row.RMK || '',
              code: v_code,
              qrCode: '',
            }
            v_label.qrCode = await QRCode.toDataURL(v_code, { width: 260, height: 260, margin: 1 })
            const previewUrl = await renderFabPreview(v_label)
            return { key: index, label: v_label, previewUrl, copies: '1' }
          })
        )
        this.printItems = items
        this.showPreview = true
      } catch (err) {
        this.$dialog.alert({ title: '生成预览失败', message: (err && err.message) || '请重试' })
      } finally {
        toast.clear()
        this.isPreparingPrint = false
      }
    },

    // 审核操作
    async stfgRec(item, val) {
      if (val !== 'Y' || item.STFG !== 'N' || this.cfmBool || this.busy || this.listLoading) return
      this.auditingId = String(item.IDSEQ)
      let loadingToast
      try {
        try {
          await this.$dialog.confirm({
            title: '审核确认',
            message: '确定审核送货单 ' + item.IDNO + ' 吗？',
            confirmButtonColor: '#235EFF',
            confirmButtonText: '确认审核',
            cancelButtonText: '取消',
          })
        } catch (error) {
          return
        }
        loadingToast = this.$toast.loading({ message: '正在审核…', forbidClick: true, duration: 0 })
        const user = String(this.v_usernm || '').replace(/'/g, "''")
        const res = await this.$axios({
          method: 'post',
          url: 'xuper_auth/updateRec',
          data: {
            p_key: 'idseq',
            p_value: item.IDSEQ,
            p_table: 'po_sent',
            p_cond: " set stfg='Y',cfmuser='" + user + "',cfmdttm='" + getDate('full') + "'",
          },
        })
        if (String(res.code) !== '200') {
          throw new Error(typeof res.result === 'string' ? res.result : '审核失败，请重试')
        }

        // 审核成功从待审核列表中移除该单据
        this.tabData = this.tabData.filter(row => row.IDSEQ !== item.IDSEQ)
        const expIdx = this.expandedIds.indexOf(String(item.IDSEQ))
        if (expIdx >= 0) this.expandedIds.splice(expIdx, 1)

        let reportError = ''
        try {
          const result = await this.$axios({
            method: 'post',
            url: '/xuper_oss/call_package',
            data: {
              corpid: this.v_corpid,
              p_idseq: item.IDSEQ,
              p_action: 'POSEND_CHK#',
              p_user: this.v_username,
              p_cond: 'Y',
            },
          })
          if (String(result.code) !== '200') {
            throw new Error(typeof result.result === 'string' ? result.result : '生成质检单失败')
          }
        } catch (error) {
          reportError = error.message || '生成质检单失败'
        }

        loadingToast.clear()
        loadingToast = null
        await this.onSearch()

        if (reportError) {
          this.$dialog.alert({ title: '审核结果', message: '单据已审核，质检单处理异常：' + reportError })
        } else {
          this.$toast.success('审核成功')
        }
      } catch (error) {
        this.$dialog.alert({ title: '审核失败', message: error.message || '请求失败，请重试' })
      } finally {
        if (loadingToast) loadingToast.clear()
        this.auditingId = ''
      }
    },

    // 检索送货单（只查询未审核记录）
    async onSearch() {
      if (this.listLoading) return
      let v_cond = ''
      if (this.v_search.trim()) {
        const value = this.v_search.trim().toUpperCase().replace(/'/g, "''")
        v_cond = " and (pono like '%" + value + "%' or idno like '%" + value + "%' or supnm like '%" + value + "%' or ordno like '%" + value + "%' or adduser like '%" + value + "%')"
      }
      v_cond = " and stfg='N' " + (v_cond ? v_cond : '') + ' order by adddttm desc'
      this.listLoading = true
      this.listError = ''
      try {
        const res = await this.$axios({
          method: 'post',
          url: 'xuper_auth/getBaseData',
          data: { p_cond: v_cond, p_table: 'V_PO_SENT_MIN' },
        })
        if (String(res.code) !== '200' || !Array.isArray(res.result)) {
          throw new Error(typeof res.result === 'string' ? res.result : '送货单查询失败')
        }
        this.tabData = res.result.filter(item => item.STFG === 'N')
        // 默认将所有送货单的明细全部展开并异步加载明细数据
        this.expandedIds = this.tabData.map(item => String(item.IDSEQ))
        this.tabData.forEach(item => {
          if (!this.detailsMap[String(item.IDSEQ)]) {
            this.getDtlList(item.IDSEQ)
          }
        })
        await this.$nextTick()
        if (this.$refs.listContent) this.$refs.listContent.scrollTop = 0
        this.topShow = false
      } catch (error) {
        this.tabData = []
        this.listError = error.message || '查询失败，请重试'
      } finally {
        this.listLoading = false
      }
    },

    clearSearch() {
      this.v_search = ''
      this.onSearch()
    },

    async initData() {
      try {
        const res = await this.$axios({
          method: 'post',
          url: 'xuper_auth/getBaseData',
          data: {
            p_key: 'isqry',
            p_value: 'M',
            p_key2: 'frmurl',
            p_value2: this.$route.query.path,
            p_table: 'V_Sys_form',
          },
        })
        if (String(res.code) === '200' && Array.isArray(res.result) && res.result.length) {
          this.v_title = res.result[0].CFRMNM || this.v_title
        }
      } catch (error) {
        // 标题获取失败使用默认值
      }
      try {
        const res = await this.$axios({
          method: 'post',
          url: 'xuper_auth/getBaseData',
          data: {
            p_key: 'userno',
            p_value: this.v_username,
            p_key2: 'frmidseq',
            p_value2: '419827',
            p_table: 'v_sys_user_priv',
          },
        })
        const row = String(res.code) === '200' && Array.isArray(res.result) ? res.result[0] : null
        this.cfmBool = !row || row.ISCFM !== 'Y'
        this.editBool = !row || row.ISUPD === 'N'
      } catch (error) {
        this.cfmBool = true
        this.$toast.fail('审核权限读取失败，请重新进入页面')
      }
    },

    onClickLeft() {
      if (!this.busy) this.$router.push({ path: '/features' })
    },

    onListScroll(event) {
      this.topShow = event.target.scrollTop > 240
    },

    goTop() {
      const list = this.$refs.listContent
      if (list) list.scrollTo({ top: 0, behavior: 'smooth' })
    },
  },
}
</script>

<style scoped lang="scss">
@import '@/assets/css/scss/variables.scss';

.po-confirm {
  height: 100vh;
  height: 100dvh;
  display: flex;
  flex-direction: column;
  background: #f7f8fa;
  color: $text-primary;
  font: 14px/1.45 $font-family-base;
}

// 头部顶栏（对标 waitChk.vue 与 stk_check.vue）
.query-header {
  flex: none;
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 48px;
  padding: 4px 8px;
  background: white;
  border-bottom: 1px solid #ebedf0;
}

.back-button {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 0;
  background: transparent;
  color: $text-primary;

  &:disabled {
    color: $text-tertiary;
  }
}

.search-form {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  gap: 6px;
}

.search-input {
  min-width: 0;
  flex: 1;
  height: 32px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 10px;
  border: 1px solid $border-color;
  border-radius: 16px;
  background: #f7f8fa;
  color: $text-secondary;

  &:focus-within {
    border-color: $primary-color;
    background: white;
  }

  input {
    width: 0;
    min-width: 0;
    flex: 1;
    height: 30px;
    outline: none;
    border: none;
    background: transparent;
    font: inherit;
    font-size: 13px;
    color: $text-primary;
  }

  input::-webkit-search-cancel-button {
    display: none;
  }
}

.clear-button {
  width: 28px;
  height: 28px;
  flex: none;
  border: 0;
  background: transparent;
  color: $text-secondary;
}

.header-action-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex: none;
  padding: 0;
  border: 1px solid #ebedf0;
  border-radius: 6px;
  background: white;
  color: $text-primary;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);

  &:active {
    background: #f0f2f5;
  }

  &:disabled {
    color: $text-tertiary;
    box-shadow: none;
  }
}

.is-spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  100% {
    transform: rotate(360deg);
  }
}

// 主列表滚动区域
.list-content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  padding: 8px;
  padding-bottom: calc(8px + env(safe-area-inset-bottom));
}

.list-summary-bar {
  max-width: 760px;
  margin: 0 auto 6px;
  padding: 0 2px;
}

.summary-text {
  font-size: 12px;
  color: $text-secondary;
}

.state-panel {
  min-height: 180px;
  padding: 28px 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 12px;
  text-align: center;
  color: $text-secondary;
}

// 主单卡片样式（紧凑且层级清晰）
.chk-card {
  max-width: 760px;
  margin: 0 auto 10px;
  padding: 10px 12px;
  border: 1px solid #d0d7de;
  border-radius: 8px;
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  margin-bottom: 6px;
}

.card-title-group {
  display: flex;
  align-items: center;
  gap: 5px;
  min-width: 0;
  flex: 1;
}

.card-seq {
  flex: none;
  min-width: 18px;
  height: 18px;
  line-height: 18px;
  padding: 0 4px;
  border-radius: 9px;
  font-size: 11px;
  font-weight: 600;
  text-align: center;
  background: #f0f2f5;
  color: $text-secondary;
}

.card-tag {
  flex: none;
  padding: 0 4px;
  border-radius: 3px;
  font-size: 10px;
  line-height: 16px;
  background: #edf2ff;
  color: $primary-color;
  font-weight: 500;
}

.card-idno {
  min-width: 0;
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
  overflow-wrap: anywhere;
  word-break: break-all;
}

// 右上角状态标签（紧凑型）
.status-tag {
  flex: none;
  border-radius: 3px;
  padding: 0 5px;
  font-size: 11px;
  line-height: 18px;
  font-weight: 500;
}

.is-pending {
  background: #fff0ed;
  color: #ff4000;
  border: 1px solid #ffd6cc;
}

.is-audited {
  background: #edf2ff;
  color: $primary-color;
}

// 展开/收起按钮（紧凑微胶囊）
.toggle-expand-btn {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  height: 22px;
  padding: 0 6px;
  border: 1px solid #dcdfe6;
  border-radius: 3px;
  background: #f8f9fa;
  color: $primary-color;
  font-size: 11px;
  font-weight: 500;
  flex: none;

  &:active {
    background: #ebedf0;
  }
}

// 单据核心信息区（紧凑网格）
.document-info {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 2px 8px;
  margin: 0;
  font-size: 12px;
  line-height: 19px;

  > div {
    display: grid;
    grid-template-columns: 52px minmax(0, 1fr);
    align-items: baseline;
    gap: 4px;
    min-width: 0;
  }

  dt {
    font-size: 11.5px;
    color: $text-secondary;
    white-space: nowrap;
  }

  dd {
    margin: 0;
    overflow-wrap: anywhere;
    word-break: break-all;
    color: $text-primary;
    font-size: 12px;
  }
}

.full-row {
  grid-column: 1 / -1;
}

.highlight-text {
  font-weight: 600;
  color: #103b9a;
}

.count-and-expand-row {
  display: flex !important;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  margin-top: 1px;
}

.count-item-wrap {
  display: grid;
  grid-template-columns: 52px minmax(0, 1fr);
  align-items: baseline;
  gap: 4px;
  min-width: 0;
}

.count-tag-wrap {
  display: flex;
  align-items: center;
}

.count-badge {
  display: inline-block;
  padding: 0 5px;
  border-radius: 3px;
  background: #f0f5ff;
  color: $primary-color;
  font-weight: 600;
  font-size: 11px;
  line-height: 16px;
}

// 就地展开容器（紧凑排版）
.expanded-details {
  margin-top: 8px;
  padding: 8px;
  background: #f8fafc;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
}

.details-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 5px;
}

.details-title {
  font-size: 12px;
  font-weight: 600;
  color: $text-primary;
}

.details-right-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

.details-badge {
  font-size: 11px;
  color: $text-secondary;
}

.print-tag-btn {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  height: 22px;
  padding: 0 6px;
  border: 1px solid #dcdfe6;
  border-radius: 3px;
  background: white;
  color: $primary-color;
  font-size: 11px;

  &:active {
    background: #f0f2f5;
  }

  &:disabled {
    color: $text-tertiary;
  }
}

.state-panel-inline {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 0;
  color: $text-secondary;
  font-size: 12px;
}

.empty-dtl-notice {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 8px 6px;
  background: #f8fafc;
  border-radius: 4px;
  color: $text-secondary;
  font-size: 11.5px;
}

// 物料子卡片
.dtl-card-list {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.dtl-item-card {
  padding: 6px 8px;
  border: 1px solid #cbd5e1;
  border-radius: 5px;
  background: white;
}

.dtl-heading {
  display: flex;
  gap: 5px;
  align-items: flex-start;
}

.dtl-seq-badge {
  width: 16px;
  height: 16px;
  line-height: 16px;
  flex: none;
  border-radius: 2px;
  text-align: center;
  background: #edf2ff;
  color: $primary-color;
  font-size: 10px;
  font-weight: 600;
  margin-top: 1px;
}

.dtl-mat-info {
  flex: 1;
  min-width: 0;
}

.dtl-mat-title {
  display: flex;
  flex-wrap: wrap;
  gap: 3px 5px;
  align-items: baseline;
}

.dtl-mat-no {
  font-size: 12px;
  font-weight: 600;
  color: #1a202c;
  overflow-wrap: anywhere;
  word-break: break-all;
}

.dtl-mat-nm {
  font-size: 12px;
  color: $text-primary;
  overflow-wrap: anywhere;
  word-break: break-all;
}

.dtl-mat-spec {
  margin: 1px 0 0;
  font-size: 11px;
  color: $text-secondary;
  line-height: 15px;
  overflow-wrap: anywhere;
  word-break: break-all;
}

// 明细数据网格
.dtl-meta-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 2px 8px;
  margin: 4px 0;
  padding: 4px 6px;
  background: white;
  border-radius: 4px;
  border: 1px solid #edf2f7;
}

.meta-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 11.5px;
  line-height: 18px;
  min-width: 0;
}

.meta-label {
  color: $text-secondary;
  white-space: nowrap;
}

.meta-value {
  font-weight: 500;
  color: $text-primary;
  overflow-wrap: anywhere;
  word-break: break-all;
  font-variant-numeric: tabular-nums;
}

.send-qty-item {
  .send-val {
    font-weight: 700;
    color: $primary-color;
  }
}

.dtl-remark-row {
  display: flex;
  align-items: baseline;
  gap: 4px;
  padding: 3px 6px;
  margin-top: 3px;
  font-size: 11.5px;
  background: white;
  border-radius: 4px;
  border: 1px solid #edf2f7;

  .remark-text {
    flex: 1;
    min-width: 0;
    color: $text-secondary;
    overflow-wrap: anywhere;
    word-break: break-all;
  }
}

// 卡片底部操作按钮区域（左编辑、中打印、右审核）
.card-footer-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #e5e7eb;
}

.action-btn {
  flex: none;
  min-width: 66px;
  height: 26px;
  line-height: 24px;
  padding: 0 12px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 13px;
  color: #fff !important;
}

.edit-btn {
  background: #1989fa !important;
  border-color: #1989fa !important;

  &:active {
    background: #0d70d6 !important;
    border-color: #0d70d6 !important;
  }
}

.print-btn {
  background: #235eff !important;
  border-color: #235eff !important;

  &:active {
    background: #1444c7 !important;
    border-color: #1444c7 !important;
  }
}

.audit-btn {
  background: #1ecd84 !important;
  border-color: #1ecd84 !important;

  &:active {
    background: #12a66a !important;
    border-color: #12a66a !important;
  }
}

// 编辑弹窗
.edit-popup {
  background: #f7f8fa;
}

.edit-popup-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  background: white;
  border-bottom: 1px solid #ebedf0;
}

.edit-popup-title {
  font-size: 14px;
  font-weight: 600;
  color: $text-primary;
}

.close-icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  margin: -8px -8px -8px 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: $text-secondary;
  cursor: pointer;

  &:active {
    color: $text-primary;
    background: rgba(0, 0, 0, 0.05);
    border-radius: 50%;
  }
}

.edit-popup-content {
  flex: 1;
  overflow-y: auto;
  padding: 8px 10px;
}

.edit-item-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.edit-item-card {
  padding: 8px 10px;
  background: white;
  border-radius: 6px;
  border: 1px solid #ebedf0;
}

.edit-item-heading {
  display: flex;
  align-items: center;
  gap: 6px;
}

.edit-mat-title {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px 6px;
  min-width: 0;
  flex: 1;
}

.edit-mat-no {
  font-size: 13px;
  font-weight: 600;
  color: $text-primary;
}

.edit-mat-nm {
  font-size: 12.5px;
  color: $text-primary;
}

.edit-mat-spec {
  margin: 2px 0 6px 22px;
  font-size: 11.5px;
  color: $text-secondary;
}

.edit-fields-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px 8px;
  padding-top: 6px;
  border-top: 1px dashed #f0f2f5;
}

.edit-field-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;

  .field-label {
    flex: none;
    color: $text-secondary;
  }

  .field-val {
    color: $text-primary;
    font-weight: 500;
  }
}

.edit-field-input {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;

  .field-label {
    font-size: 11px;
    color: $text-secondary;
  }
}

.input-unit-wrap {
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;

  .edit-input {
    padding-right: 30px;
  }
}

.edit-input {
  box-sizing: border-box;
  width: 100%;
  height: 28px;
  padding: 0 6px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #fdfdfd;
  font-size: 12px;
  color: $text-primary;
  outline: none;

  &:focus {
    border-color: $primary-color;
    background: white;
  }
}

.input-unit {
  position: absolute;
  right: 6px;
  font-size: 11px;
  color: $text-tertiary;
  pointer-events: none;
}

.edit-popup-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 8px 14px;
  background: white;
  border-top: 1px solid #ebedf0;
}

.popup-cancel-btn {
  width: 80px;
  height: 32px;
  border-radius: 16px;
}

.popup-save-btn {
  width: 90px;
  height: 32px;
  border-radius: 16px;
}

.primary-button {
  border: 0;
  border-radius: 6px;
  background: $primary-color;
  color: white;
}

// 返回顶部按钮
.go_top_btn {
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #fff;
  border-radius: 50%;
  border: 1px solid #dcdcdc;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  position: fixed;
  right: 16px;
  bottom: 24px;
  z-index: 99;
  cursor: pointer;
}

.po-print-popup {
  z-index: 2100 !important;
}

.po-print-preview {
  display: flex;
  flex-direction: column;
  height: 100%;

  > .van-nav-bar {
    flex-shrink: 0;
  }
}

.po-print-action {
  min-height: 44px;
  padding: 0 4px;
  border: 0;
  background: transparent;
  color: #1989fa;
  font-size: 14px;
  cursor: pointer;

  &:disabled {
    color: #999;
    cursor: default;
  }
}

.po-print-content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 12px 8px;
}

.po-print-counts {
  max-width: 560px;
  margin: 0 auto 16px;
  padding: 0 12px;
  border-radius: 6px;
  background: #fff;
}

.po-print-counts-heading {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 4px 12px;
  padding: 12px 0;
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.po-print-total {
  color: #666;
  font-size: 12px;
  font-weight: 400;
}

.po-print-count-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
  border-top: 1px solid #eee;
}

.po-print-material {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  gap: 2px;
  overflow-wrap: anywhere;
  font-size: 14px;
  line-height: 20px;
}

.po-print-material-code {
  font-weight: 600;
  color: #333;
}

.po-print-location {
  box-sizing: border-box;
  width: 28%;
  min-width: 0;
  height: 36px;
  padding: 0 6px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 13px;
}

.po-print-material-name {
  color: #666;
}

.po-print-count-input {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 6px;
  color: #666;
  font-size: 13px;

  input {
    box-sizing: border-box;
    width: 72px;
    height: 36px;
    padding: 0 6px;
    border: 1px solid #ddd;
    border-radius: 4px;
    background: #f7f8f9;
    color: #333;
    text-align: center;
    font-size: 14px;

    &:focus {
      outline: 2px solid #1989fa;
      outline-offset: 1px;
    }
  }
}

.po-print-label {
  max-width: 560px;
  margin: 0 auto 16px;
  text-align: center;

  p {
    margin: 0 0 8px;
    color: #666;
    font-size: 13px;
  }

  img {
    display: block;
    width: 100%;
    height: auto;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }
}

// 物料缩略图及预览样式（对标 stk_check.vue）
.material-images {
  display: grid;
  grid-template-columns: repeat(auto-fill, 48px);
  gap: 6px;
  margin-top: 6px;
}

.material-image-status {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 6px 0 0;
  font-size: 11px;
  color: $text-secondary;

  button {
    min-width: 48px;
    height: 26px;
    padding: 0 4px;
    border: 0;
    background: transparent;
    color: $primary-color;
    font-size: 11px;
  }
}

.material-thumbnail {
  position: relative;
  display: block;
  min-width: 0;
  width: 100%;
  padding: 0;
  border: 1px solid #ebedf0;
  border-radius: 4px;
  overflow: hidden;
  background: #f7f8fa;

  &::before {
    display: block;
    padding-top: 100%;
    content: '';
  }

  ::v-deep .van-image {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
}

.material-image-error {
  font-size: 10px;
  color: $text-tertiary;
}

.material-preview-close {
  position: fixed;
  top: calc(12px + env(safe-area-inset-top));
  right: 12px;
  z-index: 1;
  min-width: 48px;
  height: 36px;
  padding: 0 12px;
  border: 0;
  border-radius: 18px;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  font-size: 14px;
}

@media (max-width: 360px) {
  .po-print-count-row {
    flex-wrap: wrap;
    gap: 8px;
  }
  .po-print-material {
    flex-basis: 100%;
  }
  .po-print-location {
    flex: 1;
  }
  .po-print-counts-heading > span {
    min-width: 0;
    overflow-wrap: anywhere;
  }
}
</style>
